import { Popover, PopoverAnchor, PopoverContent } from '@syncanix/ui';
import { Search } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useT } from '@/core/providers/I18nProvider';
import { useSettingsSearch } from '@/hooks/useSettingsSearch';
import { commandChordLabel } from '@/lib/platform';
import { jumpToSetting } from '@/lib/setting-jump';
import { rankSettingsSearch } from '@/lib/settings-search-match';

import { searchOptionId } from './search-option-id';
import { SearchResultsList } from './SearchResultsList';

import type { SearchResultRow } from './SearchResultsList';
import type { SidebarItem } from '@/components/Sidebar/Sidebar';
import type { SettingsSearchEntry } from '@/lib/settings-search-index';
import type { ReactElement } from 'react';

/**
 * The visible settings search in the dashboard top bar.
 *
 * The dashboard's settings live on about fifteen pages, most of them behind
 * tabs, and until now the only way to find one was to know which page and which
 * tab it was on. There WAS an answer — the ⌘K palette — and nothing on screen
 * said so, which makes it an answer for people who already knew.
 *
 * So: a real field. Type a word, see the matching options with the page and tab
 * they live on, pick one, and land on the exact card or control with it marked
 * for a moment. ⌘K stays, reads the same index, and its chord is printed in the
 * field so the shortcut is discoverable rather than folklore.
 *
 * Desktop only. Below `md` the bar has no room for a field beside the workspace
 * switcher, so the phone gets an icon that opens the palette dialog instead
 * (`SettingsSearchTrigger`) — the same index, in the shape a phone can hold.
 *
 * ## The pattern, and the two parts of it that are easy to get wrong
 *
 * It is the ARIA combobox: the input keeps the focus the whole time, the arrow
 * keys move a highlight that never moves focus, and `aria-activedescendant`
 * names the highlighted row. Two consequences:
 *
 *  - the popover must NOT take focus when it opens, or the first keystroke goes
 *    somewhere else. Radix would autofocus it, so that is prevented explicitly;
 *  - it must not take focus BACK when it closes either. Choosing a result
 *    navigates and then focuses the card that was asked for, and a closing
 *    overlay restoring focus to the input would undo exactly that.
 */

/** Results only ever open in response to typing — see `rankSettingsSearch`. */
function rowsFor(
  results: readonly SettingsSearchEntry[],
  navigate: (to: string) => unknown,
  close: () => void,
): readonly SearchResultRow[] {
  return results.map((entry) => ({
    key: entry.id,
    label: entry.label,
    trail: entry.breadcrumb,
    onSelect: () => {
      close();
      if (entry.settingKey === undefined) {
        void navigate(entry.href);
        return;
      }
      jumpToSetting(navigate, entry.settingKey, entry.controlId);
    },
  }));
}

export function SettingsSearch({
  sidebarItems,
}: {
  readonly sidebarItems: readonly SidebarItem[];
}): ReactElement {
  const { t } = useT();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const entries = useSettingsSearch(sidebarItems);
  const results = useMemo(() => rankSettingsSearch(entries, query), [entries, query]);
  // Open on what was typed, not on focus: a dropdown listing the whole index the
  // moment the field is clicked is a wall rather than an answer.
  const open = query.trim().length > 0;

  const close = (): void => {
    setQuery('');
    setHighlight(0);
  };

  const rows = rowsFor(results, navigate, close);
  const active = results[highlight];

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h + 1) % results.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h - 1 + results.length) % results.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      rows[highlight]?.onSelect();
    } else if (event.key === 'Escape') {
      // Clears the query rather than only closing the list: an Escape that left
      // the text sitting there would reopen the same results on the next
      // keystroke, which reads as the key not working.
      event.preventDefault();
      close();
    }
  };

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <div className="flex w-full max-w-[22rem] items-center gap-2 rounded-md border border-hairline bg-surface-well px-2.5 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring lg:max-w-md">
          <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            {...(active === undefined
              ? {}
              : { 'aria-activedescendant': searchOptionId(listId, active.id) })}
            // Placeholder AND accessible name: `ui-design.md` §6 forbids a
            // placeholder standing IN PLACE of a label, which this does not — the
            // field has a real name that happens to read the same.
            aria-label={t('settingsSearch.placeholder')}
            placeholder={t('settingsSearch.placeholder')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            // §12: a search box is the one place a bare `"off"` is the idiomatic
            // value — Chrome does not profile-fill a search field.
            autoComplete="off"
            className="h-8 w-full min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
          />
          {/* The shortcut printed where the shortcut is used. Modifier symbols
              are platform names rather than copy, so they are not translated —
              and `aria-hidden` because the chord is a visual affordance, not
              something to read out mid-field. */}
          <kbd
            aria-hidden="true"
            // `dir="ltr"`: a keyboard chord is a left-to-right token. The ⌘ is
            // bidi-NEUTRAL, so in an Arabic or Hebrew page it takes the
            // paragraph direction and the chip renders "K⌘".
            dir="ltr"
            className="hidden shrink-0 rounded border border-hairline px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground lg:inline-block"
          >
            {commandChordLabel('K')}
          </kbd>
        </div>
      </PopoverAnchor>
      <PopoverContent
        // Radix defaults this to `role="dialog"`, which axe then wants a name
        // for — and which is the wrong semantics anyway: the popup in a
        // combobox IS the listbox, and the input already points at it with
        // `aria-controls`. A dialog wrapper announces a second container that
        // the pattern never mentions.
        role="presentation"
        align="start"
        // Not modal: the page behind stays scrollable and clickable, because the
        // field is a shortcut rather than a task to finish.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          // Choosing a result lands focus on the card that was asked for.
          // Returning it to the input here would take it straight back.
          event.preventDefault();
        }}
        onInteractOutside={close}
        className="w-[var(--radix-popover-trigger-width)] max-w-[min(28rem,calc(100vw-1.5rem))] p-0"
      >
        <SearchResultsList
          id={listId}
          ariaLabel={t('settingsSearch.results')}
          rows={rows}
          highlight={highlight}
          onHighlight={setHighlight}
          emptyLabel={t('palette.empty')}
          className="max-h-[min(24rem,60dvh)] overflow-y-auto"
        />
      </PopoverContent>
    </Popover>
  );
}
